import {
  IMPERSONATION_TOKEN_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
} from 'src/config-global';

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

export type ActiveAuthTokens = AuthTokens & {
  isImpersonating: boolean;
};

const getWindow = (targetWindow?: Window) => {
  if (targetWindow) return targetWindow;
  if (typeof window === 'undefined') return null;
  return window;
};

const getStorageArea = (
  targetWindow: Window | null,
  type: 'local' | 'session',
) => {
  if (!targetWindow) return undefined;

  try {
    return type === 'local'
      ? targetWindow.localStorage
      : targetWindow.sessionStorage;
  } catch (error) {
    console.error(error);
  }

  return undefined;
};

const readTokens = (storage: Storage | undefined, key: string) => {
  if (!storage) return null;

  try {
    const value = storage.getItem(key);
    if (!value) return null;

    const parsed = JSON.parse(value);
    if (
      parsed &&
      typeof parsed.accessToken === 'string' &&
      typeof parsed.refreshToken === 'string'
    ) {
      return parsed as AuthTokens;
    }
  } catch (error) {
    console.error(error);
  }

  return null;
};

const writeTokens = (
  storage: Storage | undefined,
  key: string,
  tokens: AuthTokens,
) => {
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(tokens));
  } catch (error) {
    console.error(error);
  }
};

const removeTokens = (storage: Storage | undefined, key: string) => {
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (error) {
    console.error(error);
  }
};

export const getPrincipalTokens = () => {
  const targetWindow = getWindow();
  return readTokens(getStorageArea(targetWindow, 'local'), TOKEN_STORAGE_KEY);
};

export const setPrincipalTokens = (tokens: AuthTokens) => {
  const targetWindow = getWindow();
  writeTokens(getStorageArea(targetWindow, 'local'), TOKEN_STORAGE_KEY, tokens);
};

export const removePrincipalTokens = () => {
  const targetWindow = getWindow();
  removeTokens(getStorageArea(targetWindow, 'local'), TOKEN_STORAGE_KEY);
};

export const getImpersonationTokens = (targetWindow?: Window) => {
  const resolvedWindow = getWindow(targetWindow);
  return readTokens(
    getStorageArea(resolvedWindow, 'session'),
    IMPERSONATION_TOKEN_STORAGE_KEY,
  );
};

export const setImpersonationTokens = (
  tokens: AuthTokens,
  targetWindow?: Window,
) => {
  const resolvedWindow = getWindow(targetWindow);
  writeTokens(
    getStorageArea(resolvedWindow, 'session'),
    IMPERSONATION_TOKEN_STORAGE_KEY,
    tokens,
  );
};

export const removeImpersonationTokens = (targetWindow?: Window) => {
  const resolvedWindow = getWindow(targetWindow);
  removeTokens(
    getStorageArea(resolvedWindow, 'session'),
    IMPERSONATION_TOKEN_STORAGE_KEY,
  );
};

export const getActiveAuthTokens = (): ActiveAuthTokens | null => {
  const impersonationTokens = getImpersonationTokens();

  if (impersonationTokens) {
    return {
      ...impersonationTokens,
      isImpersonating: true,
    };
  }

  const principalTokens = getPrincipalTokens();

  if (principalTokens) {
    return {
      ...principalTokens,
      isImpersonating: false,
    };
  }

  return null;
};
